import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface Props {
  disabled?: boolean;
  onTranscribed: (text: string) => void;
  /** A campo vuoto il microfono e' IL tasto della barra (arancione, al posto
   *  di «Invia»): in cantiere la domanda si detta. Appena si scrive una
   *  lettera, ChatInput lo smonta e mette «Invia» (22/09/2026, direzione A+). */
  evidenza?: boolean;
}

type Phase = "idle" | "recording" | "transcribing";
type InvokeError = Error & { context?: { status?: number; text?: () => Promise<string> } };

function errorName(err: unknown): string {
  return err instanceof Error ? err.name || err.message : String(err || "?");
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err || "");
}

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  const candidates = [
    "audio/mp4",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
  ];
  for (const t of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(t)) return t;
    } catch {
      continue;
    }
  }
  return undefined;
}

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function VoiceRecorderButton({ disabled, onTranscribed, evidenza = false }: Props) {
  const [phase, setPhaseState] = useState<Phase>("idle");
  const [elapsed, setElapsed] = useState(0);
  const [tapLocked, setTapLockedState] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const phaseRef = useRef<Phase>("idle");
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const cancelledRef = useRef(false);
  const stoppingRef = useRef(false);
  const tapLockedRef = useRef(false);
  const startedAtRef = useRef(0);
  const pressStartedAtRef = useRef(0);
  const ignoreReleaseUntilRef = useRef(0);
  const pendingStopAfterStartRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const releaseCleanupRef = useRef<(() => void) | null>(null);
  const globalReleaseHandlerRef = useRef<(event: Event, source: string) => void>(() => {});
  const permissionGrantedRef = useRef<boolean>(false);

  const setPhase = useCallback((next: Phase) => {
    phaseRef.current = next;
    setPhaseState(next);
  }, []);

  const setTapLocked = useCallback((next: boolean) => {
    tapLockedRef.current = next;
    setTapLockedState(next);
  }, []);

  const removeGlobalReleaseListeners = useCallback(() => {
    releaseCleanupRef.current?.();
    releaseCleanupRef.current = null;
  }, []);

  const cleanup = useCallback(() => {
    removeGlobalReleaseListeners();
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
    stoppingRef.current = false;
    pendingStopAfterStartRef.current = false;
    setTapLocked(false);
  }, [removeGlobalReleaseListeners, setTapLocked]);

  const installGlobalReleaseListeners = useCallback(() => {
    if (releaseCleanupRef.current) return;

    const onPointerUp = (event: PointerEvent) => globalReleaseHandlerRef.current(event, "global pointerup");
    const onTouchEnd = (event: TouchEvent) => globalReleaseHandlerRef.current(event, "global touchend");
    const onTouchCancel = (event: TouchEvent) => globalReleaseHandlerRef.current(event, "global touchcancel");
    const onPointerCancel = (event: PointerEvent) => globalReleaseHandlerRef.current(event, "global pointercancel");
    const onMouseUp = (event: MouseEvent) => globalReleaseHandlerRef.current(event, "global mouseup");
    const onBlur = (event: FocusEvent) => globalReleaseHandlerRef.current(event, "window blur");
    const onVisibilityChange = (event: Event) => {
      if (document.visibilityState === "hidden") globalReleaseHandlerRef.current(event, "visibility hidden");
    };
    const opts: AddEventListenerOptions = { capture: true, passive: false };

    document.addEventListener("pointerup", onPointerUp, opts);
    document.addEventListener("touchend", onTouchEnd, opts);
    document.addEventListener("touchcancel", onTouchCancel, opts);
    document.addEventListener("pointercancel", onPointerCancel, opts);
    document.addEventListener("mouseup", onMouseUp, opts);
    window.addEventListener("pointerup", onPointerUp, opts);
    window.addEventListener("touchend", onTouchEnd, opts);
    window.addEventListener("touchcancel", onTouchCancel, opts);
    window.addEventListener("pointercancel", onPointerCancel, opts);
    window.addEventListener("mouseup", onMouseUp, opts);
    window.addEventListener("blur", onBlur, { capture: true });
    document.addEventListener("visibilitychange", onVisibilityChange, { capture: true });
    releaseCleanupRef.current = () => {
      document.removeEventListener("pointerup", onPointerUp, opts);
      document.removeEventListener("touchend", onTouchEnd, opts);
      document.removeEventListener("touchcancel", onTouchCancel, opts);
      document.removeEventListener("pointercancel", onPointerCancel, opts);
      document.removeEventListener("mouseup", onMouseUp, opts);
      window.removeEventListener("pointerup", onPointerUp, opts);
      window.removeEventListener("touchend", onTouchEnd, opts);
      window.removeEventListener("touchcancel", onTouchCancel, opts);
      window.removeEventListener("pointercancel", onPointerCancel, opts);
      window.removeEventListener("mouseup", onMouseUp, opts);
      window.removeEventListener("blur", onBlur, { capture: true });
      document.removeEventListener("visibilitychange", onVisibilityChange, { capture: true });
    };
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  // Permesso microfono: prova a leggere lo stato persistente del browser e un flag locale
  // per evitare prompt/messaggi superflui dopo il primo consenso.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (localStorage.getItem("voice_mic_granted") === "1") {
        permissionGrantedRef.current = true;
      }
    } catch {
      // ignore
    }
    const nav = typeof navigator !== "undefined" ? navigator : null;
    const perms = nav?.permissions as
      | { query?: (d: { name: PermissionName }) => Promise<PermissionStatus> }
      | undefined;
    if (!perms?.query) return;
    let status: PermissionStatus | null = null;
    const onChange = () => {
      if (!status) return;
      if (status.state === "granted") {
        permissionGrantedRef.current = true;
        try { localStorage.setItem("voice_mic_granted", "1"); } catch { /* ignore */ }
      } else if (status.state === "denied") {
        permissionGrantedRef.current = false;
        try { localStorage.removeItem("voice_mic_granted"); } catch { /* ignore */ }
      }
    };
    perms
      .query({ name: "microphone" as PermissionName })
      .then((s) => {
        status = s;
        onChange();
        s.addEventListener?.("change", onChange);
      })
      .catch(() => {
        // Safari/iOS: API non sempre supportata, lasciamo gestire al SO
      });
    return () => {
      status?.removeEventListener?.("change", onChange);
    };
  }, []);

  // Hint "tieni premuto per parlare": le prime volte, e per pochi secondi.
  // Prima restava a schermo finché l'utente non aveva dettato due volte: chi
  // non usa mai la dettatura se lo trovava appiccicato sopra i contenuti per
  // sempre.
  //
  // E CHI NON DETTA MAI? (26/08/2026) Il contatore cresceva solo a dettatura
  // RIUSCITA. Per chi la voce non la usa - la maggioranza - "le prime volte"
  // non finivano mai: sei secondi di fumetto sopra le comunicazioni di Umberto
  // a ogni apertura, per sempre. Ora si contano anche le VOLTE CHE LO SI E'
  // MOSTRATO: dopo tre, il suggerimento ha detto la sua e si toglie di mezzo.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (Number(localStorage.getItem("voice_ptt_hint_count") || "0") >= 2) return;
      const mostrato = Number(localStorage.getItem("voice_ptt_hint_shown") || "0");
      if (mostrato >= 3) return;
      localStorage.setItem("voice_ptt_hint_shown", String(mostrato + 1));
    } catch {
      // Storage negato: si mostra comunque, e' un suggerimento di sei secondi.
    }
    setShowHint(true);
    const t = window.setTimeout(() => setShowHint(false), 6000);
    return () => window.clearTimeout(t);
  }, []);

  const transcribe = useCallback(
    async (blob: Blob) => {
      setPhase("transcribing");
      try {
        const form = new FormData();
        const ext = blob.type.includes("mp4") ? "m4a" : blob.type.includes("ogg") ? "ogg" : "webm";
        form.append("file", blob, `recording.${ext}`);
        const { data, error } = await supabase.functions.invoke("transcribe-audio", { body: form });
        if (error) {
          const invokeError = error as InvokeError;
          const ctx = invokeError.context;
          let bodyText = "";
          try {
            bodyText = await ctx?.text?.();
          } catch {
            bodyText = "";
          }
          console.error("transcribe-audio HTTP error", ctx?.status, bodyText || invokeError.message);
          // Il testo del server (porta chiusa, controllo non riuscito) si
          // mostra com'e': e' scritto per chi usa l'app (05/10/2026).
          let messaggio = "";
          try {
            const corpo = JSON.parse(bodyText);
            messaggio = typeof corpo?.messaggio === "string" ? corpo.messaggio : "";
          } catch {
            messaggio = "";
          }
          throw Object.assign(new Error(messaggio || invokeError.message), {
            context: { status: ctx?.status },
            messaggio,
          });
        }
        const text = typeof data?.text === "string" ? data.text.trim() : "";
        if (!text) toast("Non ho colto nulla. Riprova.");
        else {
          onTranscribed(text);
          try {
            const seen = Number(localStorage.getItem("voice_ptt_hint_count") || "0");
            localStorage.setItem("voice_ptt_hint_count", String(seen + 1));
            if (seen + 1 >= 2) setShowHint(false);
          } catch {
            // ignore
          }
        }
      } catch (err: unknown) {
        console.error("transcribe-audio error", err);
        // Dal 05/09 la dettatura ha un tetto settimanale e chiede l'accesso:
        // sono risposte del server, non guasti, e vanno dette con le loro parole.
        const stato = (err as InvokeError)?.context?.status;
        if (stato === 429) {
          toast.error("Dettature della settimana finite", {
            description: "Scrivi la domanda, oppure riprova da lunedì.",
          });
        } else if (stato === 401) {
          toast.error("Per dettare serve aver fatto l'accesso.");
        } else if ((stato === 403 || stato === 503) && (err as { messaggio?: string })?.messaggio) {
          // La dettatura segue la porta della chat (audit 04/10/2026).
          toast.error((err as { messaggio: string }).messaggio);
        } else if (!navigator.onLine) {
          toast.error("Nessuna connessione: la dettatura non è partita. Riprova quando torna il segnale.");
        } else {
          toast.error("Trascrizione fallita. Riprova.");
        }
      } finally {
        setPhase("idle");
        setElapsed(0);
      }
    },
    [onTranscribed, setPhase],
  );

  // Avvio sincrono dentro user gesture: getUserMedia chiamato SUBITO, niente await prima.
  const beginRecording = useCallback(() => {
    if (phaseRef.current !== "idle" || disabled) return;
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      toast.error("Registrazione audio non supportata.");
      return;
    }
    const p = navigator.mediaDevices.getUserMedia({ audio: true });
    setPhase("recording"); // ottimistico, badge visibile subito
    setShowHint(false);
    // feedback aptico (Android). iOS Safari non supporta vibrate: salta in silenzio.
    try {
      navigator.vibrate?.(50);
    } catch {
      // ignore
    }
    installGlobalReleaseListeners();
    startedAtRef.current = Date.now();
    setElapsed(0);
    timerRef.current = window.setInterval(() => {
      setElapsed((Date.now() - startedAtRef.current) / 1000);
    }, 200);
    cancelledRef.current = false;
    stoppingRef.current = false;
    pendingStopAfterStartRef.current = false;
    setTapLocked(false);
    chunksRef.current = [];

    p.then((stream) => {
      permissionGrantedRef.current = true;
      try { localStorage.setItem("voice_mic_granted", "1"); } catch { /* ignore */ }
      // se nel frattempo l'utente ha rilasciato (cancel), chiudi subito
      if (cancelledRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        cleanup();
        setPhase("idle");
        setElapsed(0);
        return;
      }
      streamRef.current = stream;
      const mimeType = pickMimeType();
      let recorder: MediaRecorder;
      try {
        recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      } catch (e: unknown) {
        cleanup();
        setPhase("idle");
        toast.error("Registrazione non supportata.");
        return;
      }
      recorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      recorder.onstop = () => {
        const wasCancelled = cancelledRef.current;
        const type = recorder.mimeType || mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        cleanup();
        if (wasCancelled || blob.size < 1200) {
          setPhase("idle");
          setElapsed(0);
          if (!wasCancelled) toast("Registrazione troppo breve.");
          return;
        }
        void transcribe(blob);
      };
      try {
        recorder.start(250);
        if (pendingStopAfterStartRef.current) {
          pendingStopAfterStartRef.current = false;
          window.setTimeout(() => {
            try {
              if (recorder.state !== "inactive") recorder.stop();
            } catch (e: unknown) {
              console.error("pending stop error", errorName(e));
            }
          }, 120);
        }
      } catch (e: unknown) {
        cleanup();
        setPhase("idle");
      }
    }).catch((err: unknown) => {
      const name = errorName(err);
      cleanup();
      setPhase("idle");
      setElapsed(0);
      if (name === "NotAllowedError" || name === "SecurityError") {
        try { localStorage.removeItem("voice_mic_granted"); } catch { /* ignore */ }
        permissionGrantedRef.current = false;
        toast.error("Permesso microfono negato. Abilitalo nelle impostazioni.");
      } else if (name === "NotFoundError") {
        toast.error("Microfono non trovato.");
      } else {
        toast.error("Impossibile accedere al microfono.");
      }
    });
  }, [cleanup, disabled, installGlobalReleaseListeners, setPhase, setTapLocked, transcribe]);

  const endRecording = useCallback(
    (cancel: boolean) => {
      if (phaseRef.current !== "recording" && !recorderRef.current) return;
      if (stoppingRef.current && recorderRef.current) return;
      stoppingRef.current = true;
      cancelledRef.current = cancel;
      setTapLocked(false);
      removeGlobalReleaseListeners();
      const rec = recorderRef.current;
      if (rec && rec.state !== "inactive") {
        try {
          try {
            rec.requestData?.();
          } catch {
            // ignore
          }
          rec.stop();
        } catch (e: unknown) {
          cleanup();
          setPhase("idle");
          setElapsed(0);
        }
      } else {
        // grant non ancora arrivato: cleanup quando arriva (vedi beginRecording)
        if (phaseRef.current === "recording" && !streamRef.current) {
          pendingStopAfterStartRef.current = !cancel;
        } else {
          cleanup();
          setPhase("idle");
          setElapsed(0);
        }
      }
    },
    [cleanup, removeGlobalReleaseListeners, setPhase, setTapLocked],
  );

  globalReleaseHandlerRef.current = (event: Event, source: string) => {
    if (phaseRef.current !== "recording" || stoppingRef.current) return;
    const target = event.target;
    if (target instanceof Element && target.closest("[data-voice-control='true']")) {
      return;
    }

    const now = Date.now();
    const isRelease = event.type === "pointerup" || event.type === "touchend" || event.type === "mouseup";
    if (tapLockedRef.current && isRelease && now < ignoreReleaseUntilRef.current) return;

    const pressedFor = now - pressStartedAtRef.current;
    if (!tapLockedRef.current && isRelease && pressedFor < 260) {
      setTapLocked(true);
      ignoreReleaseUntilRef.current = now + 450;
      return;
    }

    try {
      if ("preventDefault" in event && event.cancelable) event.preventDefault();
    } catch {
      // ignore
    }
    endRecording(false);
  };

  // ESC annulla
  useEffect(() => {
    if (phase !== "recording") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") endRecording(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, endRecording]);

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    try {
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    } catch {
      // ignore
    }
    setIsPressed(true);
    if (phaseRef.current === "recording" && tapLockedRef.current) {
      endRecording(false);
      return;
    }
    if (phaseRef.current !== "idle" || disabled) return;
    pressStartedAtRef.current = Date.now();
    beginRecording();
  };
  const onPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    setIsPressed(false);
    if (phaseRef.current !== "recording") return;
    e.preventDefault();
    globalReleaseHandlerRef.current(e.nativeEvent, "button pointerup");
  };
  const onPointerCancel = (e: React.PointerEvent<HTMLButtonElement>) => {
    setIsPressed(false);
    if (phaseRef.current !== "recording") return;
    globalReleaseHandlerRef.current(e.nativeEvent, "button pointercancel");
  };
  const onTouchStart = (e: React.TouchEvent<HTMLButtonElement>) => {
    // previene long-press menu / selezione su iOS
    e.preventDefault();
    setIsPressed(true);
    if (phaseRef.current === "recording" && tapLockedRef.current) {
      endRecording(false);
      return;
    }
    if (phaseRef.current !== "idle" || disabled) return;
    pressStartedAtRef.current = Date.now();
    beginRecording();
  };
  const onTouchEnd = (e: React.TouchEvent<HTMLButtonElement>) => {
    setIsPressed(false);
    if (phaseRef.current !== "recording") return;
    e.preventDefault();
    globalReleaseHandlerRef.current(e.nativeEvent, "button touchend");
  };

  const recording = phase === "recording";
  const transcribing = phase === "transcribing";
  const pressedActive = isPressed || recording;

  return (
    <div className="relative shrink-0">
      {/* ANCORATA A DESTRA, NON CENTRATA SUL PULSANTE (26/08/2026, dal Mac).
          Era `left-1/2 -translate-x-1/2`: centrata sul microfono, che sta a una
          sessantina di pixel dal bordo destro. La barra e' larga il doppio, e su
          iPhone meta' finiva fuori schermo - "Annulla" si leggeva "Annull" e
          non si toccava per intero. Visto sul simulatore iPhone 17 Pro (402pt).
          Vale anche per Android: dipende dalla larghezza, non dal sistema. */}
      {recording && (
        <div
          className="absolute -top-20 right-0 z-50 flex flex-col items-center gap-1"
          aria-live="polite"
        >
          <div className="flex items-center gap-2 whitespace-nowrap rounded-full border border-destructive/30 bg-card px-3 py-1.5 text-xs font-medium text-card-foreground shadow-lg">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive/80 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-destructive" />
            </span>
            <span className="font-semibold text-destructive">REC</span>
            <span className="font-mono tabular-nums">{formatTime(elapsed)}</span>
            {tapLocked && (
              <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">tap</span>
            )}
            <button
              type="button"
              data-voice-control="true"
              onClick={() => endRecording(false)}
              className="rounded-full bg-primary px-2.5 py-0.5 text-[11px] font-semibold text-primary-foreground hover:bg-primary/90"
            >
              Invia
            </button>
            <button
              type="button"
              data-voice-control="true"
              onClick={() => endRecording(true)}
              className="rounded-full px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted"
            >
              Annulla
            </button>
          </div>
          <div className="rounded-full bg-foreground/85 px-2.5 py-0.5 text-[10px] font-medium text-background shadow">
            {tapLocked ? "Tocca per inviare" : "Rilascia per inviare"}
          </div>
        </div>
      )}
      {!recording && !transcribing && showHint && (
        <div
          className="pointer-events-none absolute -top-9 right-0 z-40 whitespace-nowrap rounded-full bg-foreground/85 px-2.5 py-1 text-[10px] font-medium text-background shadow"
          aria-hidden="true"
        >
          Tieni premuto per parlare
        </div>
      )}
      <Button
        type="button"
        size="icon"
        variant={pressedActive ? "default" : "ghost"}
        disabled={disabled || transcribing}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        onContextMenu={(e) => e.preventDefault()}
        aria-label={
          transcribing
            ? "Trascrizione in corso"
            : recording
              ? tapLocked
                ? "Tocca per trascrivere"
                : "Rilascia per trascrivere"
              : "Tieni premuto per dettare"
        }
        aria-pressed={recording}
        style={{ touchAction: "none", WebkitUserSelect: "none", userSelect: "none" }}
        className={cn(
          /* 40 px, non piu' 44: i tre tasti da 44 si prendevano meta' della
             barra sul telefono (Giacomo, 11/09/2026). 40 resta un bersaglio
             da dita sporche; sotto non si scende. */
          "h-10 w-10 shrink-0 rounded-xl transition-transform",
          evidenza && !recording && !isPressed &&
            "bg-accent text-accent-foreground hover:bg-accent/90",
          recording &&
            "bg-destructive text-destructive-foreground hover:bg-destructive/90 animate-pulse scale-110 ring-4 ring-destructive/30",
          !recording && isPressed && "scale-110 bg-destructive/80 text-destructive-foreground",
        )}
      >
        {transcribing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mic className="h-5 w-5" />}
      </Button>
      {transcribing && (
        <div className="absolute -top-8 right-0 z-40 flex items-center gap-1.5 whitespace-nowrap rounded-full bg-card px-2.5 py-1 text-[11px] font-medium text-card-foreground shadow-md">
          <Loader2 className="h-3 w-3 animate-spin" />
          Trascrizione...
        </div>
      )}
    </div>
  );
}
